import {
    Box,
    Backdrop,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material";
import { useIsMutating } from "@tanstack/react-query";
import { useContext, useMemo, useState } from "react";
import { AppContext } from "../../../AppContext";
import { PosContext } from "../../../shared/PosContext";
import LogoutButton from "../../../components/LogoutButton";
import SimpleWidgetsHeader from "../../../components/SimpleWidgetsHeader/SimpleWidgetsHeader";
import SimpleWidgetsInput from "../../../components/SimpleWidgetsInput/SimpleWidgetsInput";
import { isRoleAdmin, isRolePos } from "../../../frappeConfig";
import {
    useCreateOpeningEntry,
    usePOSEntryCreateData,
    useSearchPOSOpening,
} from "../../../shared/hooks/usePOSData";
import { useSaveUserSettings } from "../../../shared/hooks/useFrappeDoctype";
import { getCurrentDate } from "../../../utils/dateUtils";
import { useStyles } from "./styles";
import { enqueueSnackbar } from "notistack";

const COMPANY_DENOMINATIONS = [0.5, 1, 2, 5, 10, 20, 50, 100, 200];

const OpeningCreateDialog = ({ dialogOpen, onConfirm }) => {
    const classes = useStyles();
    const {
        globalState,
        userInfo,
        company,
        posProfile,
        posProfiles,
        setPosProfile,
        companies,
        multipleCompanies,
    } =
        useContext(AppContext);
    const { posProfileCompanyMismatch } = useContext(PosContext);
    const createPOSOpeningEntryMutation = usePOSEntryCreateData();
    const createOpeningEntryMutation = useCreateOpeningEntry();
    const saveCompanyUserSettings = useSaveUserSettings();

    const { refetch: refetchOpeneningEntries } = useSearchPOSOpening({
        user: userInfo.name,
    });

    const [openingBalance, setOpeningBalance] = useState("");
    const [openingDenomination, setOpeningDenomination] = useState(() =>
        COMPANY_DENOMINATIONS.reduce((acc, d) => {
            acc[String(d)] = "";
            return acc;
        }, {})
    );

    const [confirmationDialog, setConfirmationDialog] = useState(false);
    const [switchingCompany, setSwitchingCompany] = useState(false);

    const isMutatin = useIsMutating();
    const isLoading = isMutatin > 0;

    const permessionAllowed =
        userInfo.name == "Administrator" ||
        isRolePos(userInfo.role_profile_name) ||
        isRoleAdmin(userInfo.role_profile_name);

    const cashDenominationEnabled = useMemo(() => {
        const v = posProfile?.cash_denomination_on_closing;
        return v === 1 || v === "1" || v === true;
    }, [posProfile?.cash_denomination_on_closing]);

    const companyCurrency = useMemo(() => {
        return posProfile?.currency || "ILS";
    }, [posProfile?.currency]);

    const currencySymbol = useMemo(() => {
        const c = String(companyCurrency || "").toUpperCase();
        if (c === "ILS") return "₪";
        if (c === "USD") return "$";
        if (c === "EUR") return "€";
        if (c === "GBP") return "£";
        return `${c} `;
    }, [companyCurrency]);

    const denominationTotal = useMemo(() => {
        return COMPANY_DENOMINATIONS.reduce((sum, d) => {
            const count = Number(openingDenomination[String(d)]) || 0;
            return sum + d * count;
        }, 0);
    }, [openingDenomination]);

    const handleChange = (value) => {
        if (/^[0-9]*$/.test(value)) {
            setOpeningBalance(value);
        }
    };

    const handleSave = (event) => {
        event.preventDefault();
        if (posProfileCompanyMismatch?.message) {
            enqueueSnackbar(posProfileCompanyMismatch.message, {
                variant: "error",
            });
            return;
        }
        if (!posProfile?.name) {
            enqueueSnackbar("Please select a POS Profile first", {
                variant: "error",
            });
            return;
        }
        // Perform necessary actions with the form data
        setConfirmationDialog(false);

        let cashValue = 0.0;
        if (cashDenominationEnabled) {
            cashValue = Number(denominationTotal) || 0;
            if (cashValue <= 0) {
                enqueueSnackbar("Please enter a valid denomination total", {
                    variant: "error",
                });
                return;
            }
        } else {
            try {
                cashValue = Number(openingBalance);
                if (isNaN(cashValue) || cashValue <= 0) {
                    cashValue = 0.0;
                }
            } catch (err) {
                enqueueSnackbar("Please enter a valid cash value", {
                    variant: "error",
                });
                return;
            }
        }

        const loggedInUser = userInfo.name;

        const entryName = "new-pos-opening-entry-3";
        const childEntryName = "new-pos-opening-entry-detail-4";

        const another = {
            docstatus: 1,
            doctype: "POS Opening Entry",
            name: entryName,
            __islocal: 1,
            // "__unsaved": 0,
            owner: loggedInUser,
            status: "Open",
            posting_date: getCurrentDate(),
            set_posting_date: 0,
            company: company.company_name,
            balance_details: [
                {
                    docstatus: 2,
                    doctype: "POS Opening Entry Detail",
                    name: childEntryName,
                    __islocal: 1,
                    __unsaved: 1,
                    owner: loggedInUser,
                    opening_amount: cashValue,
                    parent: entryName,
                    parentfield: "balance_details",
                    parenttype: "POS Opening Entry",
                    idx: 1,
                    mode_of_payment: "Cash",
                },
            ],
            pos_profile: posProfile.name,
            period_start_date: getCurrentDate(),
            user: loggedInUser,
        };

        createOpeningEntryMutation.mutate(
            {
                company: company.company_name,
                pos_profile: posProfile.name,
                balance_details: another.balance_details,
            },
            {
                onSuccess: () => {
                    refetchOpeneningEntries();
                    if (onConfirm) onConfirm();
                },
            }
        );

        // createPOSOpeningEntryMutation.mutate(
        //     {
        //         branchId: globalState.branchName,
        //         formData: another,
        //     },
        //     {
        //         onSuccess: () => {
        //             refetchOpeneningEntries();
        //             if (onConfirm) onConfirm();
        //         },
        //     }
        // );
    };

    const handleCompanyChange = async (event) => {
        const selectedCompanyName = event.target.value;
        const selectedCompany = (companies || []).find(
            (comp) => comp.name === selectedCompanyName
        );

        if (!selectedCompany || selectedCompany.name === company?.name) return;

        setSwitchingCompany(true);
        try {
            await saveCompanyUserSettings.mutateAsync({
                doctype: "Company",
                user_settings: {
                    selectedProfile: selectedCompany.name,
                },
            });
            window.location.reload();
        } catch (error) {
            setSwitchingCompany(false);
            enqueueSnackbar("Failed to switch company", {
                variant: "error",
            });
        }
    };

    const handlePosProfileChange = (event) => {
        const profileName = event.target.value;
        if (!profileName) return;
        setPosProfile(profileName);
    };

    return (
        <Dialog
            className={classes.BackdropBlur}
            open={dialogOpen}
            PaperProps={{
                sx: {
                    width: "880px",
                    maxWidth: "95vw",
                },
            }}
        >
            <Backdrop open={isLoading}>
                <CircularProgress />
            </Backdrop>
            <div className={classes.AddExtraWidget}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 8px 0 8px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            minWidth: 0,
                            flexWrap: "nowrap",
                        }}
                    >
                        {multipleCompanies && (companies || []).length > 1 ? (
                            <FormControl
                                variant="outlined"
                                size="small"
                                style={{ minWidth: "240px" }}
                            >
                                <InputLabel id="opening-company-select-label">
                                    Company
                                </InputLabel>
                                <Select
                                    labelId="opening-company-select-label"
                                    value={company?.name || ""}
                                    label="Company"
                                    onChange={handleCompanyChange}
                                    disabled={switchingCompany}
                                >
                                    {(companies || []).map((comp) => (
                                        <MenuItem key={comp.name} value={comp.name}>
                                            {comp.company_name || comp.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : null}
                        {(posProfiles || []).length > 1 ? (
                            <FormControl
                                variant="outlined"
                                size="small"
                                style={{ minWidth: "240px" }}
                            >
                                <InputLabel id="opening-pos-profile-select-label">
                                    POS Profile
                                </InputLabel>
                                <Select
                                    labelId="opening-pos-profile-select-label"
                                    value={posProfile?.name || ""}
                                    label="POS Profile"
                                    onChange={handlePosProfileChange}
                                    disabled={switchingCompany}
                                >
                                    {(posProfiles || []).map((profile) => (
                                        <MenuItem key={profile.name} value={profile.name}>
                                            {profile.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : null}
                    </Box>
                    <LogoutButton refreshOnSuccess />
                </div>
                {permessionAllowed ? (
                    <>
                        <div className={classes.AddPaymentWidget}>
                            <div className={classes.SmallTablePlaceholderWrapper}>
                                <SimpleWidgetsHeader title="INITIAL CASH VALUE" />
                            </div>
                            <div className={classes.Container}>
                                {cashDenominationEnabled ? (
                                    <div
                                        style={{
                                            width: "100%",
                                            display: "grid",
                                            gridTemplateColumns:
                                                "repeat(2, minmax(130px, 1fr))",
                                            gap: "10px",
                                        }}
                                    >
                                        {COMPANY_DENOMINATIONS.map((d) => {
                                            const count =
                                                openingDenomination[String(d)] || "";
                                            return (
                                                <TextField
                                                    key={d}
                                                    size="small"
                                                    type="number"
                                                    label={`${currencySymbol}${d} count`}
                                                    value={count}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        if (!/^[0-9]*$/.test(v)) return;
                                                        setOpeningDenomination((prev) => ({
                                                            ...prev,
                                                            [String(d)]: v,
                                                        }));
                                                    }}
                                                    inputProps={{ min: 0, step: 1 }}
                                                />
                                            );
                                        })}
                                        <Typography
                                            style={{
                                                gridColumn: "1 / -1",
                                                fontWeight: 700,
                                                marginTop: "4px",
                                            }}
                                        >
                                            Total: {currencySymbol}
                                            {denominationTotal.toFixed(2)}
                                        </Typography>
                                    </div>
                                ) : (
                                    <SimpleWidgetsInput
                                        value={openingBalance}
                                        onChange={handleChange}
                                        label="Cash Value"
                                        onKeyDown={(e) => {
                                            e.key === "Enter" &&
                                                setConfirmationDialog(true);
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                        <div className={classes.Actions}>
                            <Button onClick={() => setConfirmationDialog(true)}>
                                Save
                            </Button>
                        </div>
                    </>
                ) : (
                    <div className={classes.SmallTablePlaceholderWrapper}>
                        <SimpleWidgetsHeader title="POS IN NOT OPEN" />
                        <div className={classes.Container}>
                            Please Contact Casheir or Supervisor
                        </div>
                    </div>
                )}
            </div>

            <Dialog
                open={confirmationDialog}
                onClose={() => setConfirmationDialog(false)}
                aria-labelledby="opening-balance-confirmation"
                aria-describedby="opening-balance-confirmation"
            >
                <DialogTitle id="alert-dialog-title">
                    {`Opening Balance : ${
                        cashDenominationEnabled
                            ? `${currencySymbol}${denominationTotal.toFixed(2)}`
                            : openingBalance
                    }`}
                </DialogTitle>
                <DialogActions>
                    <Button onClick={() => setConfirmationDialog(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave}>Confirm</Button>
                </DialogActions>
            </Dialog>
            <Backdrop
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 2,
                    position: "absolute",
                    backgroundColor: "rgba(33, 43, 54, 0.75)",
                }}
                open={switchingCompany}
            >
                <CircularProgress />
            </Backdrop>
        </Dialog>
    );
};

export default OpeningCreateDialog;
